import _ from 'underscore';

import type { ReviewPartSchema } from 'bundles/assess-common/types/NaptimeReviewSchema';
import type { ReviewPart, ReviewParts } from 'bundles/assess-common/types/Reviews';

type SchemaFilter = (reviewSchemaPart: ReviewPartSchema) => boolean;
export function genSchemaFilter(submissionSchemaPartId?: string | null): SchemaFilter {
  return (reviewSchemaPart: ReviewPartSchema) => {
    if (submissionSchemaPartId === undefined) {
      return true;
    } else if (submissionSchemaPartId === null) {
      return !reviewSchemaPart.submissionPartId;
    } else {
      return reviewSchemaPart.submissionPartId === submissionSchemaPartId;
    }
  };
}

type PairFinder = (reviewSchemaPart: ReviewPartSchema) => {
  reviewSchemaPart: ReviewPartSchema;
  reviewPart?: ReviewPart;
};
export function genSchemaReviewPairFinder(reviewParts?: ReviewParts): PairFinder {
  // @ts-expect-error TSMIGRATION
  return (reviewSchemaPart: ReviewPartSchema) => {
    return {
      reviewSchemaPart,
      reviewPart: (reviewParts && reviewParts[reviewSchemaPart.id]) || null,
    };
  };
}

export function extractScore(reviewSchemaPart?: ReviewPartSchema, reviewPart?: ReviewPart): number | null {
  if (!reviewSchemaPart || !reviewPart) {
    return null;
  }

  switch (reviewPart.typeName) {
    case 'boxViewDocumentAnnotation': {
      return 0;
    }
    case 'multiLineInput': {
      if (reviewPart.definition.score) {
        return reviewPart.definition.score;
      }
      return 0;
    }
    case 'options': {
      if (reviewPart.definition.choice && reviewSchemaPart.details.typeName === 'options') {
        const selectedOption = _.findWhere(reviewSchemaPart.details.definition.options, {
          id: reviewPart.definition.choice,
        });
        if (selectedOption && selectedOption.points) {
          return selectedOption.points;
        }
      }
      return 0;
    }
    case 'singleLineInput': {
      return 0;
    }
    case 'yesNo': {
      const { choice } = reviewPart.definition;
      if (choice && reviewSchemaPart.details.typeName === 'yesNo') {
        if (choice === 'Yes') {
          return reviewSchemaPart.details.definition.points.yes;
        } else {
          return reviewSchemaPart.details.definition.points.no;
        }
      }
      return 0;
    }
    default:
      return 0;
  }
}

export function extractMaxScore(reviewSchemaPart?: ReviewPartSchema): number | null {
  if (!reviewSchemaPart) {
    return null;
  }

  switch (reviewSchemaPart.details.typeName) {
    case 'multiLineInput': {
      if (typeof reviewSchemaPart.details.definition.points === 'number') {
        return reviewSchemaPart.details.definition.points;
      }
      return 0;
    }
    case 'options': {
      const pointValues: Array<number> = reviewSchemaPart.details.definition.options.map(
        (option) => option.points || 0
      );
      return _.max([...pointValues, 0]);
    }
    case 'yesNo': {
      return _.max([reviewSchemaPart.details.definition.points.yes, reviewSchemaPart.details.definition.points.no, 0]);
    }
    // all others - box, single line
    default:
      return 0;
  }
}

export function calculateScoreForPart(
  reviewSchemaParts: Array<ReviewPartSchema> = [],
  reviewParts?: ReviewParts,
  submissionSchemaPartId?: string | null
): number | null {
  const filteredPairs = reviewSchemaParts
    .filter(genSchemaFilter(submissionSchemaPartId))
    .map(genSchemaReviewPairFinder(reviewParts));

  return filteredPairs.length === 0
    ? null
    : filteredPairs.reduce((score, pair) => {
        // @ts-expect-error TSMIGRATION
        return score + extractScore(pair.reviewSchemaPart, pair.reviewPart);
      }, 0);
}

export function calculateMaxScoreForPart(
  reviewSchemaParts: Array<ReviewPartSchema> = [],
  submissionSchemaPartId?: string
): number | null {
  const filteredParts = reviewSchemaParts.filter(genSchemaFilter(submissionSchemaPartId));

  return filteredParts.length === 0
    ? null
    : filteredParts.reduce((score, part) => {
        // @ts-expect-error TSMIGRATION
        return score + extractMaxScore(part);
      }, 0);
}

export function calculateScore(reviewParts?: ReviewParts, reviewSchemaParts?: Array<ReviewPartSchema>): number {
  if (!reviewParts || !reviewSchemaParts) {
    return 0;
  }

  const result = calculateScoreForPart(reviewSchemaParts, reviewParts);
  return result || 0;
}

export function calculateMaxScore(reviewSchemaParts: Array<ReviewPartSchema>): number {
  const result = calculateMaxScoreForPart(reviewSchemaParts);
  return result || 0;
}

export function calculateCompletion(
  reviewParts: ReviewParts | null,
  reviewSchemaParts: Array<ReviewPartSchema>
): { totalSections: number; sectionsComplete: number } {
  const totalSections = reviewSchemaParts.length;
  let sectionsComplete = 0;

  reviewSchemaParts.forEach((reviewSchemaPart: ReviewPartSchema) => {
    const partId = reviewSchemaPart.id;

    if (reviewParts && reviewParts[partId]) {
      const reviewPart = reviewParts[partId];

      switch (reviewPart.typeName) {
        case 'boxViewDocumentAnnotation': {
          // note: inline feedback is optional
          sectionsComplete += 1;
          break;
        }
        case 'multiLineInput': {
          // @ts-expect-error TSMIGRATION
          if (reviewSchemaPart.details.definition.points != null) {
            // if this is a scored multiLineInput, only score must be filled out.
            if (reviewPart.definition.score != null && reviewPart.definition.score >= 0) {
              sectionsComplete += 1;
            }
          } else {
            // In staff grading, the text feedback is optional.
            sectionsComplete += 1;
          }
          break;
        }
        case 'options': {
          if (reviewPart.definition.choice != null) {
            sectionsComplete += 1;
          }
          break;
        }
        case 'singleLineInput': {
          // the text feedback is optional.
          sectionsComplete += 1;
          break;
        }
        case 'yesNo': {
          if (reviewPart.definition.choice != null) {
            sectionsComplete += 1;
          }
          break;
        }
        default:
          break;
      }
    }
  });

  return {
    totalSections,
    sectionsComplete,
  };
}
